import type { Clock, KV } from './types';

const KEY = 'wavie.auth.redirects';

/**
 * Caps how often we hand the browser to the IdP. An OAuth deployment can
 * always produce a deterministic bounce (error redirects, an org scope the
 * user lost, a misconfigured client) — without a cap the app and the IdP
 * ping-pong forever, which is exactly the "logged in but stuck in a dead
 * loop" failure reported against web-app-v3. Counting lives in per-tab
 * (session) storage so one wedged tab can't lock every tab out.
 */
export class RedirectLoopBreaker {
  constructor(
    private readonly storage: KV,
    private readonly clock: Clock,
    private readonly maxRedirects: number,
    private readonly windowMs: number,
  ) {}

  private timestamps(): number[] {
    const raw = this.storage.get(KEY);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw) as number[];
      return Array.isArray(parsed) ? parsed.filter((t) => this.clock.now() - t < this.windowMs) : [];
    } catch {
      return [];
    }
  }

  allow(): boolean {
    return this.timestamps().length < this.maxRedirects;
  }

  record(): void {
    const stamps = this.timestamps();
    stamps.push(this.clock.now());
    this.storage.set(KEY, JSON.stringify(stamps));
  }

  /** Call on a completed login: a real success ends the loop-suspicion window. */
  reset(): void {
    this.storage.remove(KEY);
  }
}
